import { Loader, Sidebar } from 'components/common';
import UserShortCut from 'components/common/UserShortCut';
import {
  deleteIntervention,
  getInterventionById,
} from 'features/material/services/interventionServices';
import { useParams } from 'next/navigation';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { hasPermission } from 'utils/permissions';
import HistoryIntervention from './HistoryIntervention';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import Tippy from '@tippyjs/react';
import CloseIntervention from './CloseIntervention';
import { useMediaContext } from 'contexts/MediaContext';
import EditIntervention from './EditIntervention';
import { useDialog } from 'contexts/DialogContext';
import AffectedusersIntervention from './AffectedusersIntervention';
import useFetchData from 'hooks/useFetch';
import NewActionIntervention from './NewActionIntervention';

interface InfoInterventionProps {
  id: number;
  refetch: () => void;
  toggleRefreshDT?: () => void;
}

const InfoIntervention: FC<SideBarProps<InfoInterventionProps>> = ({
  sidebar,
  setSidebarWidth,
  sidebarWidth,
  params,
}) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { openSidebar, closeAllSidebars } = useSidebarManager();
  const { openMedia } = useMediaContext();
  const dialog = useDialog();

  const {
    data: intervention,
    refetch: interventionRefetch,
    loading,
  } = useFetchData(getInterventionById, [params.id]);

  const validatePdfOrImg = (fileName: string): boolean => {
    const ext = fileName.split('.').pop()?.toLowerCase() as string;
    const extImg = ['png', 'jpg', 'jpeg', 'jfif'];
    const extPdf = ['pdf'];
    return extImg.includes(ext) || extPdf.includes(ext);
  };

  const handleOpenFile = (
    fileName: string,
    title: string,
    id: number,
  ): void => {
    const ext = fileName.split('.').pop()?.toLowerCase() as string;
    openMedia(`/upload/VueFile/${id}/`, ext, title);
  };

  const getStatusColor = (status?: string): string => {
    if (!status) return '';

    switch (status) {
      case 'Solved':
        return 'success';
      case 'Pending':
      case 'Progressing':
        return 'warning';
      case 'Affected':
        return 'info';
      case 'Closed':
        return 'danger';
      default:
        return '';
    }
  };

  const handleDelete = async () => {
    dialog
      .delete('hardware.profile.Delete_title', 'conge.Delete_description')
      .then(async (res) => {
        if (res) {
          const response = await deleteIntervention(params.id);
          if (response.success) {
            toast.success(t('Default.succesMessage'));
            closeAllSidebars();
            params?.refetch?.();
            params?.toggleRefreshDT?.();
          }
        }
      });
  };

  if (!intervention) return null;

  return (
    <Sidebar
      size="m"
      type="primary"
      sidebar={sidebar}
      setSidebarWidth={setSidebarWidth}
      sidebarWidth={sidebarWidth}
    >
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="minthr-flex minthr-flex-items-center minthr-pb-2 minthr-mb-3 minthr-border-b minthr-border-b-3">
            <div className="minthr-icon-bg minthr-icon-bg-l minthr-icon-bg-secondary minthr-icon-circle minthr-mr-3">
              <img
                className="minthr-icon-m"
                src="/icons/intervention.svg"
                alt=""
              />
            </div>
            <h3>{t('intervention.info.Title')}</h3>

            <button
              className="minthr-sidebar-close"
              type="button"
              onClick={() => {
                closeAllSidebars();
              }}
            >
              <img src="/icons/close.svg" alt="" />
            </button>
          </div>
          <div className="minthr-pt-2">
            {/* @Html.HiddenFor(x=>x.Id) */}
            <div className="minthr-flex minthr-flex-items-center minthr-flex-space-between">
              <h5 className="minthr-color-primary minthr-mb-3">
                {intervention?.data?.title}&nbsp;
                {intervention?.data?.priority && (
                  <Tippy
                    content={t(
                      `intervention.priority.${intervention?.data?.priority.toLowerCase()}`,
                    )}
                  >
                    <img
                      src={`/icons/priority-${intervention?.data?.priority.toLowerCase()}.svg`}
                      alt=""
                      className="minthr-icon-m"
                    />
                  </Tippy>
                )}
              </h5>
              {/* @if (await PermissionCheck.HasPermission("Intervention.Edit") || (await PermissionCheck.HasPermission("Profil.Intervention.Edit") && User.GetUserId() == Model.Fk_DemadeBy)) */}
              {(hasPermission('Intervention.Edit') ||
                (hasPermission('Profil.Intervention.Edit') &&
                  id == intervention?.data?.fk_DemadeBy)) && (
                <button
                  type="button"
                  className="minthr-btn minthr-btn-s minthr-btn-icon-origin minthr-ml-auto minthr-px-1"
                  onClick={() => {
                    openSidebar(EditIntervention, false, {
                      id: params.id,
                      refetch: interventionRefetch,
                      parentRefetch: params?.refetch,
                    });
                  }}
                >
                  <img src="/icons/edit.svg" className="minthr-icon-m" alt="" />
                </button>
              )}

              {hasPermission('Intervention.Delete') && (
                <button
                  type="button"
                  data-delete="true"
                  // data-action="@Url.Action("DeleteIntervention","Material", new { Id = Model.Id})" data-text="@SharedLocalizer.Get("conge.Delete_description")" data-title="@SharedLocalizer.Get("hardware.profile.Delete_title")"
                  onClick={handleDelete}
                  className="minthr-btn minthr-btn-s minthr-btn-icon-origin minthr-icon-danger minthr-px-1"
                >
                  <img
                    src="/icons/delete.svg"
                    className="minthr-icon-m"
                    alt=""
                  />
                </button>
              )}
            </div>
            <div className="row g-0 minthr-flex-items-center minthr-mb-1">
              <div className="col-sm-4">
                <span className="minthr-text-uppercase p">
                  {t('Intervention.Statut')}
                </span>
              </div>
              <div className="col-sm-8">
                <div
                  className={`minthr-badge minthr-badge-${getStatusColor(intervention?.data?.statut)} minthr-badge-s minthr-px-2`}
                >
                  {t(`intervention.statut.${intervention?.data?.statut}`)}
                </div>
              </div>
            </div>

            <div className="row g-0 minthr-flex-items-center minthr-mb-1">
              <div className="col-sm-4">
                <span className="minthr-text-uppercase p">
                  {t('Intervention.filter.TypeIntervention')}
                </span>
              </div>
              <div className="col-sm-8">
                <div className="minthr-color-dark h5">
                  {intervention?.data?.typeIntervention}
                </div>
              </div>
            </div>
            <div className="row g-0 minthr-flex-items-center minthr-mb-1">
              <div className="col-sm-4">
                <span className="minthr-text-uppercase p">
                  {t('intervention.info.F.DemandBy')}
                </span>
              </div>
              <div className="col-sm-8">
                <div className="minthr-flex minthr-flex-items-center minthr-color-dark h5">
                  {/* @Model.DemandeByUser.FullName */}
                  {intervention?.data?.demandeByUser?.fullName}
                  {/* <img className="minthr-avatar minthr-avatar-s minthr-ml-2" src="@Url.Action("GetProfileImage", "Files", new { filename = Model.DemandeByUser.PicturePath, userid = Model.DemandeByUser.Id })" alt=""> */}
                  <UserShortCut
                    src={`upload/profileimages/${intervention?.data?.demandeByUser?.id}/${intervention?.data?.demandeByUser?.picturePath}`}
                    alt={intervention?.data?.demandeByUser?.fullName ?? ''}
                    className="minthr-avatar minthr-avatar-s minthr-ml-2"
                  />
                </div>
              </div>
            </div>
            <div className="row g-0 minthr-flex-items-center minthr-mb-1">
              <div className="col-sm-4">
                <span className="minthr-text-uppercase p">
                  {t('intervention.info.F.inventaire')}
                </span>
              </div>
              <div className="col-sm-8">
                <div className="minthr-color-dark h5">
                  {intervention?.data?.material_Title}
                </div>
              </div>
            </div>
            {/* @if (!string.IsNullOrEmpty(Model.Nature)) */}
            {intervention?.data?.nature && (
              <div className="row g-0 minthr-flex-items-center minthr-mb-1">
                <div className="col-sm-4">
                  <span className="minthr-text-uppercase p">
                    {t('intervention.info.F.nature')}
                  </span>
                </div>
                <div className="col-sm-8">
                  <div className="minthr-color-dark h5">
                    {intervention?.data?.nature}
                  </div>
                </div>
              </div>
            )}

            {/* @if (!string.IsNullOrEmpty(Model.Description)) */}
            {intervention?.data?.description && (
              <div className="row g-0 minthr-flex-items-center minthr-mb-1">
                <div className="col-sm-12 minthr-mb-2">
                  <span className="minthr-text-uppercase p">
                    {t('Hardware.Info.F.description')}
                  </span>
                </div>
                <div className="col-sm-12">
                  <div className="minthr-color-dark h5">
                    {intervention?.data?.description}
                  </div>
                </div>
              </div>
            )}

            {/* @if (Model.FilesNames?.Count()>0)
    {
        var cmpt = 0;
        <div className="row g-0 minthr-mb-5 minthr-mt-5">

            @foreach (var item in Model.FilesNames)
            {
                var filename = item.Name.Split(".");
                var ext = filename[filename.Length - 1];
                string classNamee = "";
                var icon = "eye.svg";
                var btn_text = "Visualiser";
                if (item.Mimetype == "image")
                {
                    classNamee = "vue-image";
                }
                else if (ext == "pdf")
                {
                    classNamee = "vue-pdf";
                }
                else
                {
                    classNamee = "DownloadFile";
                    icon = "download.svg";
                    btn_text = "download";
                }

                <div className="col-sm-12">
                    <div className="minthr-flex minthr-flex-items-center minthr-bg-light-gray minthr-text-decoration-none minthr-p-2 minthr-border-rounded minthr-mb-3">
                        <img className="minthr-icon-xl minthr-mr-2" src="/newtheme/dist/icons/doc-file.svg" alt="">
                        <div className="minthr-mr-6 minthr-pr-3">
                            <h6>@item.Name</h6>
                            <div className="text-s"></div>
                        </div>
                        <button type="button" className="minthr-p-0 minthr-border-none minthr-bg-transparent minthr-ml-auto minthr-mr-2 @classNamee" data-name="@item.Name" data-id="@item.Id" data-title="@item.Name" data-url="@Url.Action("VueFile","Files", new{Id = @item.Id})">
                            <img className="minthr-icon-s minthr-icon-danger" src="/newtheme/dist/icons/@icon" alt="">
                        </button>
                    </div>
                </div>
            }
        </div>
        cmpt++;
    } */}
            {intervention?.data?.filesNames &&
              intervention?.data?.filesNames?.length > 0 && (
                <div className="row g-0 minthr-mb-5 minthr-mt-5">
                  {intervention?.data?.filesNames?.map((item) => {
                    const filename = item.name?.split('.');
                    const ext = filename?.[filename.length - 1];
                    let className = '';

                    if (item.mimetype === 'image') {
                      className = 'vue-image';
                    } else if (ext === 'pdf') {
                      className = 'vue-pdf';
                    } else {
                      className = 'DownloadFile';
                    }

                    return (
                      <div key={item.id} className="col-sm-12">
                        <div className="minthr-flex minthr-flex-items-center minthr-bg-light-gray minthr-text-decoration-none minthr-p-2 minthr-border-rounded minthr-mb-3">
                          <img
                            className="minthr-icon-xl minthr-mr-2"
                            src={`/icons/${
                              ext === 'pdf'
                                ? 'doc-pdf.svg'
                                : ['jpg', 'jpeg', 'png', 'gif'].includes(
                                      ext || '',
                                    )
                                  ? 'doc-img.svg'
                                  : ['doc', 'docx'].includes(ext || '')
                                    ? 'doc-word.svg'
                                    : ['xls', 'xlsx'].includes(ext || '')
                                      ? 'doc-excel.svg'
                                      : 'doc-file.svg'
                            }`}
                            alt=""
                          />
                          <div className="minthr-mr-6 minthr-pr-3">
                            <h6>{item.name}</h6>
                            <div className="text-s"></div>
                          </div>

                          <div className="minthr-ml-auto minthr-mr-2 minthr-flex">
                            {validatePdfOrImg(item.name || '') && (
                              // View button for PDF or image files
                              <button
                                type="button"
                                className={`minthr-p-0 minthr-border-none minthr-bg-transparent minthr-mr-2 ${className}`}
                                onClick={() =>
                                  handleOpenFile(
                                    item.name || '',
                                    item.name || '',
                                    item.id,
                                  )
                                }
                              >
                                <img
                                  className="minthr-icon-s minthr-icon-danger"
                                  src="/icons/eye.svg"
                                  alt="View"
                                />
                              </button>
                            )}

                            {/* Download button for all file types */}
                            <a
                              href={`/upload/VueFile/${item.id}/`}
                              download={item.name}
                              className="minthr-p-0 minthr-border-none minthr-bg-transparent"
                            >
                              <img
                                className="minthr-icon-s minthr-icon-danger"
                                src="/icons/download.svg"
                                alt="Download"
                              />
                            </a>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            {/* @if (!string.IsNullOrEmpty(Model.History_description)) */}
            {intervention?.data?.history_description && (
              <div className="row g-0 minthr-flex-items-center minthr-mb-1">
                <div className="col-sm-4">
                  <span className="minthr-text-uppercase p">
                    {t('Hardware.Info.title.step')}
                  </span>
                </div>
                <div className="col-sm-8">
                  <div className="minthr-flex minthr-flex-items-center minthr-color-dark h5">
                    {/* @Model.History_description */}
                    {intervention?.data?.history_description}
                    {/* <img className="minthr-avatar minthr-avatar-s minthr-ml-2" src="@Url.Action("getprofileimage","Files", new {FileName=Model.WhoDid.PicturePath ?? null , userid=Model.WhoDid.Id ?? null })" alt=""> */}
                    <UserShortCut
                      src={`upload/profileimages/${intervention?.data?.whoDid?.id}/${intervention?.data?.whoDid?.picturePath}`}
                      alt={intervention?.data?.whoDid?.fullName ?? ''}
                      className="minthr-avatar minthr-avatar-s minthr-ml-2"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="row g-0 minthr-flex-items-center minthr-mb-2">
            <div className="col-sm-4">
              <span className="minthr-text-uppercase p">
                {t('Hardware.Info.title.assign')}
              </span>
            </div>
            <div className="col-sm-8">
              <div className="minthr-flex minthr-flex-items-center">
                <div className="minthr-avatar-group minthr-mr-auto minthr-ml-3">
                  {intervention?.data?.users?.slice(0, 2).map((item) => {
                    return (
                      <UserShortCut
                        key={item.id}
                        src={`upload/profileimages/${item?.id}/${item?.picturePath}`}
                        alt={item?.fullName ?? ''}
                        className="minthr-avatar minthr-avatar-m"
                      />
                    );
                  })}
                  {intervention?.data?.users &&
                    intervention?.data?.users?.length > 2 && (
                      <a
                        className="minthr-avatar minthr-avatar-m"
                        id="users-count"
                      >
                        {`+${intervention?.data?.users.length - 2}`}
                      </a>
                    )}
                </div>
                <a
                  href="javascript:void(0)"
                  // onclick="openDataInSideBar('#hardware-secend-sidebar','@Url.Action("InterventionHistory", "Material", new { Id = @Model.Id})',false)"
                  className="minthr-avatar minthr-avatar-s"
                  onClick={() => {
                    openSidebar(HistoryIntervention, false, {
                      id: intervention?.data?.id,
                      parentRefetch: interventionRefetch,
                    });
                  }}
                >
                  <img
                    className="minthr-icon-success minthr-icon-s"
                    src="/icons/replace.svg"
                    alt=""
                  />
                </a>
                {(hasPermission('Intervention.Edit') ||
                  (hasPermission('Profil.Intervention.Edit') &&
                    id == intervention?.data?.fk_DemadeBy)) && (
                  <a
                    href="javascript:void(0)"
                    id="assign"
                    className="minthr-avatar minthr-avatar-s"
                    onClick={() => {
                      openSidebar(AffectedusersIntervention, false, {
                        id: params.id,
                        affectedUsers: intervention?.data?.users || [],
                        refetch: interventionRefetch,
                        parentRefetch: params?.refetch,
                      });
                    }}
                  >
                    <img
                      className="minthr-icon-success minthr-icon-s"
                      src="/icons/plus.svg"
                      alt=""
                    />
                  </a>
                )}
              </div>
            </div>
          </div>
          {intervention?.data?.users &&
            intervention?.data?.users?.length > 0 && (
              <div
                className="user-list"
                style={{
                  display: 'none',
                  height: '200px',
                  overflowY: 'scroll',
                }}
              >
                {intervention?.data?.users?.map((item) => (
                  <div
                    key={item.id}
                    className="minthr-flex minthr-flex-items-center minthr-box minthr-border-rounded minthr-bg-white minthr-p-2 minthr-mb-2 collaboratteur"
                  >
                    <div className="minthr-avatar-m minthr-mr-2">
                      <span className="">
                        <UserShortCut
                          src={`upload/profileimages/${intervention?.data?.whoDid?.id}/${intervention?.data?.whoDid?.picturePath}`}
                          alt={intervention?.data?.whoDid?.fullName ?? ''}
                          className="minthr-avatar minthr-avatar-m minthr-mr-2"
                        />
                      </span>
                    </div>
                    <div>
                      <h5>{item.fullName}</h5>
                      <span className="minthr-icon-text">{item.fonction}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          <div className="form-details list-user d-none">
            <div className="row">
              <div
                className={`col-12 ${intervention?.data?.users && intervention?.data?.users?.length > 2 ? 'list-user-content ot-gray-scroll' : ''}`}
              >
                {/* @foreach (var item in Model.Users) */}
                {intervention?.data?.users?.map((item) => (
                  <div
                    key={item.id}
                    className="d-flex align-items-center w-100 list-user-card inventaire-userList-border-bottom pb-1"
                  >
                    {/* <img src="@Url.Action("getprofileimage","Files", new {FileName=item.PicturePath ?? null , userid=item.Id ?? null })" className="user rounded-pill inventaire-info-user" /> */}
                    <UserShortCut
                      src={`upload/profileimages/${intervention?.data?.whoDid?.id}/${intervention?.data?.whoDid?.picturePath}`}
                      alt={intervention?.data?.whoDid?.fullName ?? ''}
                      className="user rounded-pill inventaire-info-user"
                    />
                    <div className="assigned-user-details w-100 ml-1">
                      <div className="row align-items-center">
                        <div className="col">
                          <h3 className="mb-1">{item.fullName}</h3>
                          <p>{item.fonction}</p>
                        </div>
                        {hasPermission('Software.Affect') && (
                          <div className="col-auto">
                            <a
                              href="javascript:void(0)"
                              className="Anassign"
                              data-id="@item.Id"
                            >
                              <img
                                src="/icons/trash-red.svg"
                                className="deleteAffectation"
                                alt=""
                              />
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="minthr-flex minthr-flex-items-center minthr-flex-space-between">
            {/* @if (await PermissionCheck.HasPermission("Intervention.AddStep") && Model.Statut != Solved && Model.Statut != Closed) */}
            {hasPermission('Intervention.AddStep') &&
              intervention?.data?.statut != 'Solved' &&
              intervention?.data?.statut != 'Closed' && (
                <button
                  type="button"
                  className="minthr-btn minthr-btn-primary"
                  id="add-step"
                  onClick={() => {
                    openSidebar(NewActionIntervention, false, {
                      id: params.id,
                      refetchIntervention: interventionRefetch,
                    });
                  }}
                >
                  {t('new.action')}
                </button>
              )}
          </div>
          <a
            className="minthr-color-primary minthr-text-decoration-underline"
            // onclick="openDataInSideBar('#intervention-secend-sidebar', '@Url.Action("InterventionHistory", "Material", new { Id = @Model.Id})',false)"
            onClick={() => {
              openSidebar(HistoryIntervention, false, {
                id: params.id,
                parentRefetch: interventionRefetch,
              });
            }}
          >
            {t('show.history')}
            <img
              className="minthr-icon-m minthr-ml-2"
              src="/icons/back-in-time.svg"
              alt=""
            />
          </a>

          <div className="minthr-sidebar-footer minthr-box-shadow minthr-flex minthr-flex-items-center minthr-flex-space-between minthr-py-4 minthr-mx-n5 minthr-px-4 minthr-mt-auto">
            {/* @if (await PermissionCheck.HasPermission("Intervention.Close") && Model.Statut != Solved && Model.Statut != Closed) */}
            {hasPermission('Intervention.Close') &&
              intervention?.data?.statut != 'Solved' &&
              intervention?.data?.statut != 'Closed' && (
                <button
                  type="button"
                  className="minthr-btn minthr-btn-primary minthr-ml-auto"
                  // onclick="openDataInSideBar('#intervention-sidebar', '@Url.Action("CloseIntervention", "Material")/@Model.Id')"
                  onClick={() => {
                    openSidebar(CloseIntervention, false, {
                      id: params.id,
                      refetch: interventionRefetch,
                      parentRefetch: params.refetch,
                      toggleRefreshDT: params.toggleRefreshDT,
                    });
                  }}
                >
                  {t('Intervention.close')}
                </button>
              )}
          </div>
        </>
      )}
    </Sidebar>
  );
};

export default InfoIntervention;
